import { useAuth0 } from '@auth0/auth0-react'
import { useMemo } from 'react'
import Cookies from 'universal-cookie'
import { useQuery } from '@tanstack/react-query'
import useAxios from 'hooks/useAxios'
import { User } from 'types/user'
import { useIsDealerAccount, useIsMechanicAccount, useIsPartnerAccount, useIsServiceAccount } from '@utils/users'
import { GetUserByIdQueryOptions } from '@lib/api/users'

export const defaultColorSetting = '008072'
export const defaultThemeSetting = 'dark'
export const defaultMenuColorsSetting = 'show'
export const defaultLanguageSetting = 'en'
export const defaultSettingHouseUptimeSetting = 20
export const defaultDateFormatSetting = 'EEEE d MMMM'
export const defaultTimeFormatSetting = 'H:mm'

export const useUserSettings = () => {
  const { user, isAuthenticated } = useAuth0()
  const apiClient = useAxios()
  const cookies = useMemo(() => new Cookies(), [])
  const isMechanicAccount = useIsMechanicAccount(user as User)
  const isServiceAccount = useIsServiceAccount(user as User)
  const isPartnerAccount = useIsPartnerAccount(user as User)
  const isDealerAccount = useIsDealerAccount(user as User)

  const queryOptions = useMemo(() => GetUserByIdQueryOptions(apiClient, user?.app_user_id), [apiClient, user?.app_user_id])

  const { data: userSettings } = useQuery({
    ...queryOptions,
    select: (data) => data.settings,
    enabled: isAuthenticated && !!user?.app_user_id,
  })

  const settings = useMemo(() => {
    const get = (name: string) => userSettings?.find((s: any) => s.name === name)?.value

    // Check if color exists in settings (userSettings or cookies)
    const colorFromUserSettings = get('color') && get('color') !== 'undefined' ? get('color') : null
    const colorFromCookies = cookies.get('color', { doNotParse: true })
    const hasColorSetting = colorFromUserSettings || (colorFromCookies && colorFromCookies !== 'undefined')

    // If color setting exists, use it; otherwise use account-specific colors or default
    const accountColor = isMechanicAccount
      ? 'F86F00'
      : isServiceAccount
        ? '005FBB'
        : isPartnerAccount
          ? '6F42C1'
          : isDealerAccount
            ? '00A3AD'
            : defaultColorSetting

    return {
      color: hasColorSetting ? colorFromUserSettings || colorFromCookies : accountColor,
      avatar: get('avatar') && get('avatar') !== 'undefined' ? get('avatar') : cookies.get('avatar') || null,
      theme: get('theme') && get('theme') !== 'undefined' ? get('theme') : cookies.get('theme') || defaultThemeSetting,
      menuColors: cookies.get('menuColors') || defaultMenuColorsSetting,
      language: get('language') && get('language') !== 'undefined' ? get('language') : cookies.get('language') || defaultLanguageSetting,
      settingHouseUptime:
        get('settingHouseUptime') && get('settingHouseUptime') !== 'undefined'
          ? get('settingHouseUptime')
          : cookies.get('settingHouseUptime') || defaultSettingHouseUptimeSetting,
      dateFormat:
        get('dateFormat') && get('dateFormat') !== 'undefined' ? get('dateFormat') : cookies.get('dateFormat') || defaultDateFormatSetting,
      timeFormat:
        get('timeFormat') && get('timeFormat') !== 'undefined' ? get('timeFormat') : cookies.get('timeFormat') || defaultTimeFormatSetting,
    }
  }, [userSettings, isMechanicAccount, isServiceAccount, isPartnerAccount, isDealerAccount, cookies])

  return settings
}
